import React from "react";
import {CssBaseline, ThemeProvider, StyledEngineProvider} from "@mui/material";
import {ApolloClient, InMemoryCache, ApolloProvider} from "@apollo/client";
import {PortApolloClient} from "~/apollo/client";
import {createTheme} from "~/ui/theme";
import {AuthProvider} from "~/auth/components/AuthProvider";
import {WaitOpen} from "~/components/WaitOpen";
import {DeviceSupportWarning} from "~/components/DeviceSupportWarning";
import {Analytics} from "~/components/Analytics";
import {AlertErrorHandlerProvider} from "~/components/ErrorHandling";
import {YMInitializer} from "react-yandex-metrika";
import {DialogStackProvider} from "~/components/DialogStack";
import {BrowserRouter, Route, Switch} from "react-router-dom";
import {hot} from "react-hot-loader/root";
import IndexPage from "~/pages/IndexPage";
import ProfilePage from "~/pages/ProfilePage";
import ReleaseNotesPage from "~/pages/ReleaseNotesPage";
import InstallPage from "~/pages/instructions/InstallPage";
import NotFoundPage from "~/pages/NotFoundPage";
import VesselCallsApp from "~/apps/packages/vessel-calls";
import WarehouseClosureApp from "~/apps/packages/warehouse-closure";
import PassesApp from "~/apps/packages/passes";
import FinanceApp from "~/apps/packages/finance";
import CustomsRequirementsApp from "~/apps/packages/customs-requirements";
import AutoVisitsApp from "~/apps/packages/auto-visits";
import CargosHistoryApp from "~/apps/packages/cargos";
import {AppUpdate} from "./components/AppUpdate";
import {CustomerExperience} from "~/customers-experience";
import {NotificationProvider} from "~/notifications/NotificationProvider";
import {GlobalHistoryProvider} from "~/components/ApplicationHistory";
import AutoShipmentOrdersApp from "~/apps/packages/auto-shipment-orders";

class Application extends React.Component {
    private readonly apollo: ApolloClient<InMemoryCache> = new PortApolloClient();
    private readonly theme = createTheme();

    render() {
        return (
            <React.Fragment>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={this.theme}>
                            <CssBaseline/>
                            <AlertErrorHandlerProvider>
                                <ApolloProvider client={this.apollo}>
                                    <AuthProvider>
                                        <NotificationProvider>
                                            <Analytics/>
                                            <CustomerExperience/>
                                            <WaitOpen>
                                                <BrowserRouter>
                                                    <GlobalHistoryProvider>
                                                        <DialogStackProvider>
                                                        <AppUpdate/>
                                                        <Switch>
                                                            <Route path="/" exact>
                                                                <DeviceSupportWarning/>
                                                                <IndexPage/>
                                                            </Route>
                                                            <Route path="/profile" exact>
                                                                <ProfilePage/>
                                                            </Route>
                                                            <Route path="/release-notes" exact>
                                                                <ReleaseNotesPage/>
                                                            </Route>
                                                            <Route path="/instructions/install" exact>
                                                                <InstallPage/>
                                                            </Route>
                                                            <Route path={`/apps/auto-visits`}>
                                                                <AutoVisitsApp/>
                                                            </Route>
                                                            <Route path="/apps/customs-requirements">
                                                                <CustomsRequirementsApp/>
                                                            </Route>
                                                            <Route path="/apps/finance">
                                                                <FinanceApp/>
                                                            </Route>
                                                            <Route path="/apps/passes">
                                                                <PassesApp/>
                                                            </Route>
                                                            <Route path="/apps/warehouse-closure">
                                                                <WarehouseClosureApp/>
                                                            </Route>
                                                            <Route path="/apps/vessel-calls">
                                                                <VesselCallsApp/>
                                                            </Route>
                                                            <Route path="/apps/cargos">
                                                                <CargosHistoryApp/>
                                                            </Route>
                                                            <Route path="/apps/auto-shipment-orders">
                                                                <AutoShipmentOrdersApp/>
                                                            </Route>
                                                            <Route path="*">
                                                                <NotFoundPage/>
                                                            </Route>
                                                        </Switch>
                                                        </DialogStackProvider>
                                                    </GlobalHistoryProvider>
                                                </BrowserRouter>
                                            </WaitOpen>
                                        </NotificationProvider>
                                    </AuthProvider>
                                </ApolloProvider>
                            </AlertErrorHandlerProvider>
                    </ThemeProvider>
                </StyledEngineProvider>
                <YMInitializer accounts={window.location.host == "pp.vmtp.ru" ? [81946963] : [81961333]} options={{
                    clickmap: true,
                    trackLinks: true,
                    accurateTrackBounce: true,
                    webvisor: true
                }} version="2"/>
            </React.Fragment>
        );
    }
}


export default hot(Application);
